/**
 * Detection for "this workspace is read-only" API errors (backend codes
 * TENANT_SUSPENDED / TENANT_INACTIVE — see backend shared/tenant-operations).
 *
 * Same shape as permission-lock-detection.util.ts and deliberately store-free
 * for the same reason (api.service.ts is imported by nearly everything, so it
 * must not import the Redux store): the axios interceptor tags the error and
 * fires a window event; AdminLayout.tsx is the one listener that dispatches the
 * real Redux action and renders <TenantBlockedDialog/>.
 *
 * Only WRITES are ever refused (GETs keep working), so this fires from a
 * user-initiated save/send/delete — but a page can also fire background writes
 * (mark-as-read, heartbeats). EVENT_COOLDOWN_MS stops a burst of those from
 * stacking dialogs; the persistent banner in AdminLayout carries the state
 * regardless, so nothing is hidden by the cooldown.
 */

// Module augmentation: makes `{ suppressTenantBlocked: true }` a valid AxiosRequestConfig everywhere,
// and carries it back out on `error.config` to the interceptor (same mechanism as suppressPermissionLock).
declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface AxiosRequestConfig {
    /** Don't open the "workspace is read-only" dialog for THIS call's refusal — for background writes
     *  the user didn't initiate (e.g. mark-as-read when a chat opens). The banner still shows. */
    suppressTenantBlocked?: boolean;
  }
}

export const TENANT_SUSPENDED_CODE = 'TENANT_SUSPENDED';
export const TENANT_INACTIVE_CODE = 'TENANT_INACTIVE';

/** Fired on `window` when a write is refused because the workspace is read-only. */
export const TENANT_BLOCKED_EVENT = 'api:tenant-blocked';

const EVENT_COOLDOWN_MS = 5000;

export type TenantBlockedStatus = 'suspended' | 'inactive';

/** Where an INACTIVE workspace pays to reactivate (a suspended one has no self-service exit). */
export const REACTIVATE_ROUTE = '/admin/billing/active-plan';

export interface TenantBlockedPayload {
  status: TenantBlockedStatus;
  message: string;
}

export function isTenantBlockedCode(code?: string): boolean {
  return code === TENANT_SUSPENDED_CODE || code === TENANT_INACTIVE_CODE;
}

export function buildTenantBlockedPayload(code: string, message?: string): TenantBlockedPayload {
  const status: TenantBlockedStatus = code === TENANT_INACTIVE_CODE ? 'inactive' : 'suspended';
  return {
    status,
    message:
      message ||
      (status === 'inactive'
        ? 'This workspace is inactive. You can view your data, but no changes can be made until it is reactivated.'
        : 'This workspace has been suspended. You can view your data, but no changes can be made. Please contact support.'),
  };
}

let lastEventAt = 0;

function fire(payload: TenantBlockedPayload): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastEventAt < EVENT_COOLDOWN_MS) return;
  lastEventAt = now;
  window.dispatchEvent(new CustomEvent(TENANT_BLOCKED_EVENT, { detail: payload }));
}

/** Axios response interceptor hook — tags the error and fires the global event. */
export function tagAxiosErrorWithTenantBlocked(error: any): void {
  const apiError = error?.response?.data?.error as { code?: string; message?: string } | undefined;
  if (apiError && isTenantBlockedCode(apiError.code)) {
    const payload = buildTenantBlockedPayload(apiError.code!, apiError.message);
    error.tenantBlocked = payload;
    // A suppressed (background) refusal is still tagged, but fires nothing and — importantly — does not
    // consume the burst cooldown, which must stay available for the user's own next action.
    if (!error.config?.suppressTenantBlocked) fire(payload);
  }
}

/** Same detection for raw `fetch` responses (streaming/SSE call sites bypass axios). */
export async function detectTenantBlockedResponse(res: Response): Promise<void> {
  if (res.status !== 403) return;
  try {
    const apiError = (await res.clone().json())?.error as { code?: string; message?: string } | undefined;
    if (apiError && isTenantBlockedCode(apiError.code)) {
      fire(buildTenantBlockedPayload(apiError.code!, apiError.message));
    }
  } catch {
    /* body wasn't JSON — nothing to detect */
  }
}

/** Test-only: reset the burst cooldown. */
export function __resetTenantBlockedCooldown(): void {
  lastEventAt = 0;
}
